import { useCallback, useEffect, useRef } from 'react';

import { router, usePage } from '@inertiajs/react';

import { updateCsrfToken } from '../services/CsrfTokenService';
import { LocalStorageService } from '../services/LocalStorageService';
import { CurrentUser } from '../types';
import { LocalStorageKeys } from '../types/localStorage';

const SESSION_SYNC_INTERVAL_IN_MS = 12 * 60 * 60 * 1000;

type SessionSyncPageProps = {
    currentUser?: CurrentUser | null;
};

const currentUserId = (currentUser?: CurrentUser | null) =>
    currentUser?.id ?? null;

const recordKnownSession = (userId: number | null) => {
    LocalStorageService.setItem(
        LocalStorageKeys.SESSION_SYNC_LAST_KNOWN_USER_ID,
        userId,
    );
};

const knownSessionChanged = (userId: number | null) => {
    const lastKnownUserId = LocalStorageService.getItem(
        LocalStorageKeys.SESSION_SYNC_LAST_KNOWN_USER_ID,
    );

    return lastKnownUserId !== userId;
};

const canSyncSession = (userId: number | null) => {
    if (knownSessionChanged(userId)) {
        return true;
    }

    const lastSyncedAt = LocalStorageService.getItem(
        LocalStorageKeys.SESSION_SYNC_LAST_SYNCED_AT,
    );

    if (typeof lastSyncedAt !== 'number') {
        return true;
    }

    const elapsed = Date.now() - lastSyncedAt;

    return elapsed < 0 || elapsed >= SESSION_SYNC_INTERVAL_IN_MS;
};

const recordSessionSyncAttempt = () => {
    LocalStorageService.setItem(
        LocalStorageKeys.SESSION_SYNC_LAST_SYNCED_AT,
        Date.now(),
    );
};

export const useSessionSync = () => {
    const syncingRef = useRef(false);
    const { currentUser } = usePage<SessionSyncPageProps>().props;
    const userId = currentUserId(currentUser);

    useEffect(() => {
        recordKnownSession(userId);
    }, [userId]);

    const syncSession = useCallback(() => {
        if (syncingRef.current || !canSyncSession(userId)) {
            return;
        }

        syncingRef.current = true;
        recordSessionSyncAttempt();

        router.reload({
            only: ['currentUser', 'newNotificationPresent', 'csrfToken'],
            onSuccess: (page) => {
                const pageProps = page.props as SessionSyncPageProps;

                recordKnownSession(currentUserId(pageProps.currentUser));
                updateCsrfToken(page.props.csrfToken);
            },
            onFinish: () => {
                syncingRef.current = false;
            },
        });
    }, [userId]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                syncSession();
            }
        };
        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                syncSession();
            }
        };

        window.addEventListener('focus', syncSession);
        window.addEventListener('pageshow', handlePageShow);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', syncSession);
            window.removeEventListener('pageshow', handlePageShow);
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            );
        };
    }, [syncSession]);
};
